import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, setJsonLd, setSeo, track } from "@/lib/api";
import { EmailCapture, GuideCard, QuestionCard } from "@/components/site/Blocks";
import { SmartImage } from "@/components/site/SmartImage";
import { PLANNING_TOOLS_VISIBLE } from "@/lib/features";

// Homepage category tiles. Owner-supplied photography, homepage only: the hubs keep their own hero
// images for the category pages. Outdoor Living falls back to its existing approved hub image.
const TILE_ORDER = ["recreation", "outdoor-living", "home-power-technology"];
const TILE_IMAGE = {
  "wellness-recovery": {
    src: "/api/assets/human-home-living/editorial/home/wellness-featured.png",
    alt: "A glass-front home sauna beside a living room massage chair",
  },
  recreation: {
    src: "/api/assets/human-home-living/editorial/home/recreation-tile.png",
    alt: "A home golf simulator lounge with dark wood panelling and tall windows",
  },
  "outdoor-living": {
    src: "/api/assets/human-home-living/editorial/home/outdoor-living-tile.png",
    alt: "A black aluminium pergola over a lounge and fire pit in a landscaped backyard",
  },
  "home-power-technology": {
    src: "/api/assets/human-home-living/editorial/home/home-power-tile.png",
    alt: "A dark home battery backup unit beside a covered patio and garden",
  },
};
const PLANNING_TILE = {
  slug: "planning-tools",
  name: "Planning Tools",
  to: "/tools",
  src: "/api/assets/human-home-living/editorial/home/planning-tools-tile.png",
  alt: "A laptop on a wooden desk showing the Human Home Living planning tools",
};
// Medical & Mobility is its own section rather than a hub, so it carries its own tile. Full width
// at the end of the grid keeps the 2x2 above it symmetrical.
const MEDICAL_TILE = {
  slug: "medical-mobility",
  name: "Medical & Mobility",
  to: "/medical-mobility",
  src: "/media/medical-mobility/home-elevators-accessibility-1400.webp",
  alt:
    "A woman stepping out of a residential elevator into a bright living space, with a stair lift " +
    "on the staircase behind her",
};
const TILE_CTA = {
  "wellness-recovery": "Explore Wellness",
  recreation: "Explore Golf Simulators",
  "outdoor-living": "Explore Outdoor Living",
  "home-power-technology": "Explore Home Power",
  "planning-tools": "Explore Planning Tools",
  "medical-mobility": "Explore Medical & Mobility",
};

const HOW = [
  {
    n: "01",
    i: 1,
    t: "Understand the options",
    d: "We explain the real categories and what separates them — not a ranked list of products.",
  },
  {
    n: "02",
    i: 2,
    t: "See the trade-offs",
    d: "Every upgrade costs something else. We name what you give up, not just what you gain.",
  },
  {
    n: "03",
    i: 3,
    t: "Plan the real cost",
    d: "Planning tools account for installation, electrical, structure and the items nobody quotes.",
  },
  {
    n: "04",
    i: 4,
    t: "Make your decision",
    d: "We resolve the barrier and hand the decision back to you. You stay in control.",
  },
];

export default function Home() {
  const [data, setData] = useState(null);

  useEffect(() => {
    setSeo({
      title: "Human Home Living — Find what fits your home and the way you live",
      description:
        "Practical guides, comparisons and planning tools that help homeowners understand what fits, what it really costs and which options are worth considering.",
    });
    track({ event_type: "page_view", page: "/" });
    const origin = window.location.origin;
    setJsonLd("ld-organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Human Home Living",
      url: `${origin}/`,
      logo: `${origin}/hhl-logo-full.png?v=4`,
      sameAs: [
        "https://www.youtube.com/@humanhomeliving",
        "https://www.instagram.com/humanhomeliving/",
        "https://www.pinterest.com/humanhomeliving/",
      ],
      description:
        "Practical guides, comparisons and planning tools that help homeowners understand what fits, what it really costs and which options are worth considering.",
    });
    setJsonLd("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Human Home Living",
      url: `${origin}/`,
      publisher: { "@type": "Organization", name: "Human Home Living" },
    });
    api.get("/homepage").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const hubs = data?.hubs || [];

  return (
    <div data-testid="home-page">
      {/* SECTION 1 — HERO */}
      <section className="relative bg-bone">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-0 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-end">
          <div className="fade-up">
            <span className="eyebrow">Homeowner decision platform</span>
            <h1 className="display font-normal text-[42px] sm:text-[54px] lg:text-[64px] leading-[1.03] mt-6 text-charcoal">
              Find what fits your home and the way you live.
            </h1>
            <p className="text-[19px] md:text-[22px] font-light leading-[1.5] text-[#4F4F4F] mt-7 max-w-xl">
              Explore home saunas, golf simulators, massage chairs, generators, outdoor living and
              other major home upgrades. Compare what fits, understand the trade-offs and see your
              options when you're ready.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 mt-9 mb-6 lg:mb-20">
              <Link
                to="#hubs"
                data-testid="hero-primary-cta"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("hubs")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-charcoal text-white text-[12.5px] tracking-[0.16em] uppercase hover:bg-sage transition-colors duration-300"
              >
                Explore Products &amp; Ideas <ArrowRight size={15} />
              </Link>
              <Link
                to="#popular-decisions"
                data-testid="hero-secondary-cta"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("popular-decisions")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-charcoal/25 text-charcoal text-[12.5px] tracking-[0.16em] uppercase hover:border-sage hover:text-sage transition-colors duration-300"
              >
                See Popular Decisions
              </Link>
            </div>
          </div>

          <div className="relative lg:-mb-20 fade-up" style={{ animationDelay: "140ms" }}>
            <SmartImage
              src={data?.hero_image}
              alt="A modern home in late afternoon light"
              priority
              sizes="(min-width:1024px) 50vw, 100vw"
              maxWidth={1600}
              className="w-full h-[300px] sm:h-[420px] lg:h-[560px] object-cover"
            />
            <div className="absolute -bottom-0 left-0 bg-white/95 backdrop-blur px-6 py-5 max-w-[300px] hidden md:block border-t-2 border-sage">
              <p className="text-[15px] md:text-[15.5px] font-medium leading-[1.7] text-[#3F3F3F]">
                Home saunas · Golf simulators · Massage chairs · Outdoor kitchens · Generators
              </p>
            </div>
          </div>
        </div>
        <div className="h-0 lg:h-24" />
      </section>

      {/* SECTION 2 — HUBS */}
      <section id="hubs" className="bg-white border-y border-line px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-20 items-end mb-14">
            <div>
              <span className="eyebrow">Where to start</span>
              <h2 className="display text-[36px] md:text-[46px] leading-[1.08] mt-5 text-charcoal">
                What are you trying to improve?
              </h2>
            </div>
            <p className="text-[17px] md:text-[18px] leading-[1.6] text-[#454545] max-w-xl">
              Explore the home upgrades people research most. Choose the area you're interested in.
            </p>
          </div>

          {/* Image-led category grid: one full-width featured tile, then a symmetrical 2x2.
              Category names sit below the photography — nothing is overlaid on the images. */}
          {(() => {
            const bySlug = Object.fromEntries(hubs.map((h) => [h.slug, h]));
            const featured = bySlug["wellness-recovery"];
            const rest = TILE_ORDER.map((slug) => bySlug[slug]).filter(Boolean);
            const Tile = ({ to, testid, slug, src, alt, name, ratio, sizes, delay }) => (
              <Link
                to={to}
                data-testid={testid}
                className="group fade-up block"
                style={{ animationDelay: `${delay}ms` }}
              >
                <div className={`overflow-hidden rounded-sm bg-bone ${ratio}`}>
                  <SmartImage
                    src={src}
                    alt={alt}
                    sizes={sizes}
                    maxWidth={1600}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="display font-medium text-[24px] md:text-[28px] leading-[1.14] text-charcoal mt-4">
                  {name}
                </h3>
                <span
                  data-testid={`hub-cta-${slug}`}
                  className="inline-flex items-center gap-2 mt-3.5 px-6 py-3 rounded-[6px] bg-sage-dark text-bone text-[11.5px] tracking-[0.15em] uppercase font-semibold group-hover:bg-[#54634E] transition-colors duration-300"
                >
                  {TILE_CTA[slug]} <ArrowUpRight size={13} />
                </span>
              </Link>
            );
            return (
              <div className="space-y-7 md:space-y-9">
                {featured && (
                  <Tile
                    to={`/hub/${featured.slug}`}
                    testid={`hub-card-${featured.slug}`}
                    slug={featured.slug}
                    src={TILE_IMAGE[featured.slug].src}
                    alt={TILE_IMAGE[featured.slug].alt}
                    name={featured.name}
                    ratio="aspect-[2/1]"
                    sizes="(min-width:1280px) 1280px, 100vw"
                    delay={0}
                  />
                )}
                <div className="grid sm:grid-cols-2 gap-7 md:gap-9">
                  {rest.map((h, i) => (
                    <Tile
                      key={h.slug}
                      to={`/hub/${h.slug}`}
                      testid={`hub-card-${h.slug}`}
                      slug={h.slug}
                      src={TILE_IMAGE[h.slug]?.src || h.image}
                      alt={TILE_IMAGE[h.slug]?.alt || h.name}
                      name={h.name}
                      ratio="aspect-[16/9]"
                      sizes="(min-width:640px) 50vw, 100vw"
                      delay={(i + 1) * 80}
                    />
                  ))}
                  {PLANNING_TOOLS_VISIBLE && (
                    <Tile
                      to={PLANNING_TILE.to}
                      testid={`hub-card-${PLANNING_TILE.slug}`}
                      slug={PLANNING_TILE.slug}
                      src={PLANNING_TILE.src}
                      alt={PLANNING_TILE.alt}
                      name={PLANNING_TILE.name}
                      ratio="aspect-[16/9]"
                      sizes="(min-width:640px) 50vw, 100vw"
                      delay={320}
                    />
                  )}
                </div>
                <Tile
                  to={MEDICAL_TILE.to}
                  testid={`hub-card-${MEDICAL_TILE.slug}`}
                  slug={MEDICAL_TILE.slug}
                  src={MEDICAL_TILE.src}
                  alt={MEDICAL_TILE.alt}
                  name={MEDICAL_TILE.name}
                  ratio="aspect-[21/9]"
                  sizes="(min-width:1280px) 1280px, 100vw"
                  delay={400}
                />
              </div>
            );
          })()}
        </div>
      </section>

      {/* SECTION 3 — QUESTIONS */}
      <section className="px-5 md:px-8 pt-20 md:pt-28 pb-16 md:pb-24">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-2xl mb-14 md:mb-16">
            <span className="eyebrow">The approach</span>
            <h2 className="display text-[36px] md:text-[46px] leading-[1.08] mt-5 text-charcoal">
              Start with the question, not the product.
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.65] text-[#454545] mt-5">
              Every guide begins with a real homeowner question and names the barrier standing between
              you and a confident decision.
            </p>
          </div>
          <div
            data-testid="homepage-questions"
            className="grid md:grid-cols-2 gap-px bg-line border-y border-line"
          >
            {(data?.questions || []).map((a, i) => (
              <QuestionCard key={a.slug} a={a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL PAUSE — one wide approved image between the questions and the tools */}
      {data?.editorial_break && (
        <div
          data-testid="homepage-editorial-break"
          className="relative h-[280px] md:h-[420px] lg:h-[480px] overflow-hidden border-y border-line"
        >
          <SmartImage
            src={data.editorial_break.image}
            alt={data.editorial_break.image_alt}
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      )}

      {/* SECTION 4 — PLANNING TOOLS (temporarily hidden; the tools and their pages are untouched) */}
      {PLANNING_TOOLS_VISIBLE && (
      <section className="bg-charcoal text-white px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <span className="eyebrow eyebrow-light">Interactive</span>
              <h2 className="display text-[36px] md:text-[46px] leading-[1.08] mt-5">Planning tools</h2>
            </div>
            <Link
              to="/tools"
              data-testid="tools-index-link"
              className="text-[12.5px] tracking-[0.16em] uppercase text-[#B7C6B0] font-semibold hover:text-white transition-colors inline-flex items-center gap-2"
            >
              All planning tools <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-white/12">
            {(data?.tools || []).map((t, i) => (
              <Link
                key={t.slug}
                to={`/tools/${t.slug}`}
                data-testid={`tool-card-${t.slug}`}
                className="group bg-charcoal p-8 md:p-10 hover:bg-[#3C3C3C] transition-colors duration-500"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="display text-[19px] text-[#A9B8A2]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[11px] tracking-[0.16em] uppercase text-[#C2BFB9] border border-white/20 rounded-full px-3 py-1">
                    {t.barrier}
                  </span>
                </div>
                <h3 className="display text-[27px] md:text-[31px] leading-tight mt-5">{t.name}</h3>
                <p className="text-[16px] md:text-[17px] leading-[1.65] text-[#CBC8C2] mt-4">{t.summary}</p>
                <span className="inline-flex items-center gap-2 text-[12.5px] tracking-[0.14em] uppercase font-semibold text-white mt-7 group-hover:gap-3.5 transition-all duration-300">
                  Open tool <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* SECTION 5 — POPULAR HOME DECISIONS */}
      <section id="popular-decisions" className="px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-2xl mb-12">
            <span className="eyebrow">Decision guides</span>
            <h2 className="display text-[36px] md:text-[46px] leading-[1.08] mt-5 text-charcoal">
              Popular home decisions
            </h2>
          </div>
          <div data-testid="featured-guides" className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
            {(data?.featured || []).map((a, i) => (
              <GuideCard key={a.slug} a={a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — HOW WE HELP */}
      <section className="bg-white border-y border-line px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-xl mb-14">
            <span className="eyebrow">Our method</span>
            <h2 className="display text-[36px] md:text-[46px] leading-[1.08] mt-5 text-charcoal">
              How Human Home Living helps
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
            {HOW.map((h) => (
              <div key={h.n} data-testid={`how-step-${h.i}`}>
                <div className="display text-[15px] text-sage-dark tracking-widest">{h.n}</div>
                <div className="rule my-5" />
                <h3 className="text-[19px] md:text-[21px] font-medium text-charcoal leading-snug">{h.t}</h3>
                <p className="text-[16px] md:text-[17px] leading-[1.7] text-[#454545] mt-3.5">{h.d}</p>
              </div>
            ))}
          </div>
          <Link
            to="/how-we-help"
            data-testid="how-we-help-link"
            className="inline-flex items-center gap-2 mt-14 text-[13px] tracking-[0.16em] uppercase text-[#48563F] font-medium hover:text-charcoal transition-colors"
          >
            How we help homeowners choose <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* SECTION 7 — EMAIL */}
      <EmailCapture />
    </div>
  );
}
